import { useEffect } from "react";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { setLenis } from "@/lib/smoothScroll";
import { prefersReducedMotion } from "@/cinematic/config";

gsap.registerPlugin(ScrollTrigger);

/**
 * Instancia o Lenis (smooth scroll) sincronizado com GSAP/ScrollTrigger,
 * integrado ao ciclo de vida do React. Desativado com prefers-reduced-motion.
 */
export function useLenisScroll(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled || prefersReducedMotion()) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.5,
    });
    setLenis(lenis);
    if (import.meta.env.DEV) {
      (window as unknown as { __lenis?: Lenis }).__lenis = lenis;
    }

    lenis.on("scroll", ScrollTrigger.update);

    const tick = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    // Seções lazy alteram a altura do documento após montar;
    // refaz as posições dos gatilhos quando o layout muda.
    let refreshTimer: number | undefined;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 200);
    });
    ro.observe(document.body);

    return () => {
      ro.disconnect();
      window.clearTimeout(refreshTimer);
      gsap.ticker.remove(tick);
      lenis.destroy();
      setLenis(null);
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  }, [enabled]);
}
